import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  User,
  Lock,
  Loader2,
  Truck,
  Copy,
  Pencil,
  Power,
  PowerOff,
  Key
} from 'lucide-react';

const DriversPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: ''
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/drivers`, {
        headers: getAuthHeader()
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('حدث خطأ في تحميل السائقين');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setFormData({
      username: '',
      password: '',
      name: ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (driver) => {
    setSelectedDriver(driver);
    setDeleteDialogOpen(true);
  };

  const openPasswordDialog = (driver) => {
    setSelectedDriver(driver);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/drivers`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('تم إضافة السائق بنجاح');
      setDialogOpen(false);
      fetchDrivers();
    } catch (error) {
      console.error('Error saving driver:', error);
      if (error.response?.status === 400) {
        toast.error('اسم المستخدم موجود مسبقاً');
      } else {
        toast.error('حدث خطأ في حفظ البيانات');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/drivers/${selectedDriver.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف السائق بنجاح');
      setDeleteDialogOpen(false);
      fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error('حدث خطأ في حذف السائق');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/drivers/${selectedDriver.id}`,
        { password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('حدث خطأ في تغيير كلمة المرور');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleDriverStatus = async (driver) => {
    try {
      const newStatus = !driver.is_active;
      await axios.put(
        `${API_URL}/api/drivers/${driver.id}`,
        { is_active: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(newStatus ? 'تم تفعيل حساب السائق' : 'تم تعطيل حساب السائق');
      fetchDrivers();
    } catch (error) {
      console.error('Error toggling driver status:', error);
      toast.error('حدث خطأ في تحديث حالة السائق');
    }
  };

  const copyCredentials = (username) => {
    navigator.clipboard.writeText(username);
    toast.success('تم نسخ اسم المستخدم');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="drivers-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">إدارة السائقين</h1>
          <p className="text-stone-500 mt-1">{drivers.length} سائق</p>
        </div>
        <Button
          data-testid="add-driver-btn"
          onClick={openAddDialog}
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
        >
          <Plus className="w-5 h-5 me-2" />
          إضافة سائق
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <p className="text-blue-800 text-sm">
            <strong>ملاحظة:</strong> السائق يستطيع رؤية ورقة التوصيل فقط (الزبائن والكميات) بدون الأسعار.
            أعطِ السائق اسم المستخدم وكلمة المرور التي تنشئها هنا.
          </p>
        </CardContent>
      </Card>

      {/* Drivers List */}
      {drivers.length === 0 ? (
        <div className="empty-state">
          <Truck className="empty-state-icon" />
          <h3 className="empty-state-title">لا يوجد سائقين</h3>
          <p className="empty-state-description">
            أضف سائقاً جديداً وأعطه بيانات الدخول
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {drivers.map((driver) => (
              <Card key={driver.id} className={`border-stone-200 ${!driver.is_active ? 'opacity-60 bg-stone-100' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-stone-900">{driver.name}</h3>
                        {!driver.is_active && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">معطل</span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 mt-1 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {driver.username}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyCredentials(driver.username)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openPasswordDialog(driver)}
                        className="text-blue-600 hover:text-blue-700"
                        title="تغيير كلمة المرور"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleDriverStatus(driver)}
                        className={driver.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                        title={driver.is_active ? 'تعطيل الحساب' : 'تفعيل الحساب'}
                      >
                        {driver.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-driver-${driver.id}`}
                        onClick={() => openDeleteDialog(driver)}
                        className="text-stone-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table */}
          <Card className="hidden md:block border-stone-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-right font-bold">اسم السائق</TableHead>
                  <TableHead className="text-right font-bold">اسم المستخدم</TableHead>
                  <TableHead className="text-right font-bold">الحالة</TableHead>
                  <TableHead className="text-right font-bold">تاريخ الإنشاء</TableHead>
                  <TableHead className="text-right font-bold">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map((driver) => (
                  <TableRow key={driver.id} className={`hover:bg-stone-50/50 ${!driver.is_active ? 'opacity-60 bg-stone-100' : ''}`}>
                    <TableCell className="font-medium">{driver.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {driver.username}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyCredentials(driver.username)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {driver.is_active ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">نشط</span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">معطل</span>
                      )}
                    </TableCell>
                    <TableCell className="font-numbers">
                      {new Date(driver.created_at).toLocaleDateString('ar-SA')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openPasswordDialog(driver)}
                          className="text-blue-600 hover:text-blue-700"
                          title="تغيير كلمة المرور"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleDriverStatus(driver)}
                          className={driver.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                          title={driver.is_active ? 'تعطيل الحساب' : 'تفعيل الحساب'}
                        >
                          {driver.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-driver-${driver.id}`}
                          onClick={() => openDeleteDialog(driver)}
                          className="text-stone-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Add Driver Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة سائق جديد</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">اسم السائق *</Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="name"
                  data-testid="driver-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="ps-10"
                  placeholder="مثال: أحمد"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username">اسم المستخدم *</Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="username"
                  data-testid="driver-username-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="ps-10"
                  placeholder="مثال: driver1"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">كلمة المرور *</Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="password"
                  data-testid="driver-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="ps-10"
                  placeholder="كلمة مرور للسائق"
                  required
                />
              </div>
              <p className="text-xs text-stone-500">
                احفظ كلمة المرور لأنها لن تظهر مرة أخرى
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                data-testid="save-driver-btn"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={formLoading}
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'إضافة'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>تغيير كلمة المرور</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-stone-600">
              تغيير كلمة المرور للسائق: <strong>{selectedDriver?.name}</strong>
            </p>
            <div className="space-y-2">
              <Label htmlFor="newPassword">كلمة المرور الجديدة *</Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="ps-10"
                  placeholder="أدخل كلمة المرور الجديدة"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleChangePassword}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={formLoading || !newPassword}
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'تغيير'
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد من حذف هذا السائق؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف السائق "{selectedDriver?.name}" ولن يستطيع الدخول مرة أخرى.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-driver-btn"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DriversPage;
